import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { companyService } from '../../services/companyService';
import { authService } from '../../services/authService';
import { STORAGE_BASE_URL } from '../../config/api';
import CompanyNav from '../../components/CompanyNav';
import CompanyBottomNavigation from '../../components/CompanyBottomNavigation';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';

const Profile = () => {
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: '',
        bio: '',
        address: '',
        site_link: '',
        email: '',
        phone: '',
        employees_count: '',
        image: '',
    });

    const loadProfile = async () => {
        try {
            setLoading(true);
            const data = await companyService.getProfile();

            // Handle possible response shapes
            const company = data?.data ?? data;
            setForm({
                name: company?.name ?? '',
                bio: company?.bio ?? '',
                address: company?.address ?? '',
                site_link: company?.site_link ?? '',
                email: company?.email ?? '',
                phone: company?.phone ?? '',
                image: company?.image ?? '',
                pdf: company?.pdf ?? '',
                employees_count: company?.employees_count ?? '',
                active_subscription: company?.active_subscription ?? '',
            });
        } catch (error) {
            const message = error?.response?.data?.message || t('company.profile.loadFailed');
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProfile();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            const payload = {
                ...form,
                employees_count: form.employees_count === '' ? null : Number(form.employees_count),
            };
            await companyService.updateProfile(payload);
            toast.success(t('company.editProfile.saveSuccess'));
        } catch (error) {
            const message = error?.response?.data?.message || t('company.editProfile.saveFailed');
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Loader fullScreen={true} size="large" />;
    }

    // company avatar like employee profile
    const companyName = form.name || t('company.profile.name');
    const companyImage = form.image || user?.image;

    return (
        <div className="employee-profile-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            <CompanyNav user={user} />

            {/* Picture and name like employee profile */}
            <div className="profile-picture-container d-flex justify-content-center flex-wrap mb-4">
                <div className="profile-picture-large position-relative">
                    {companyImage ? (
                        <img
                            src={companyImage}
                            alt="Company"
                            className="profile-img-large"
                        />
                    ) : (
                        <div className="profile-img-large text-center align-content-center">
                            <b style={{ fontSize: '4rem' }}>{companyName.charAt(0).toUpperCase()}</b>
                        </div>
                    )}
                </div>
                <p className="d-block w-100 text-center">{companyName}</p>
                {form.active_subscription && (
                    <div className="status-badge-success">
                        {form.active_subscription.plan.name}
                    </div>
                )}
            </div>

            {/* Basic info section (view) */}
            <div className="profile-section px-3 mb-4">
                <div className="section-header d-flex justify-content-between align-items-center mb-2">
                    <h3 className="section-title">{t('company.profile.title')}</h3>
                </div>
                <ul className="education-details">
                    <li>{form.email || '—'}</li>
                    <li>{form.phone || '—'}</li>
                    <li>{form.site_link || '—'}</li>
                    <li>{form.address || '—'}</li>
                </ul>
            </div>

            {/* About section */}
            <div className="profile-section px-3 mb-4">
                <div className="section-header d-flex justify-content-between align-items-center mb-2">
                    <h3 className="section-title">{t('company.profile.bio')}</h3>
                </div>
                <p className="section-text">{form.bio || t('company.profile.noBio') || 'No company bio added yet.'}</p>
            </div>

            {/* PDF section */}
            {/* {form.pdf && (
                <div className="profile-section px-3 mb-4">
                    <div className="section-header d-flex justify-content-between align-items-center mb-2">
                        <h3 className="section-title">{t('company.profile.pdf') || 'Company PDF'}</h3>
                    </div>
                    <a
                        href={form.pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-view-vacancy"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            textDecoration: 'none'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                            <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        {t('company.profile.viewPdf') || 'View PDF'}
                    </a>
                </div>
            )} */}
            {/* Current PDF Display */}
            {form.pdf && (
                <div className="pdf-preview-card" style={{
                    background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f2ff 100%)',
                    border: '2px dashed #4a7bc4',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)'
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: '#111', marginBottom: '4px' }}>
                                {t('company.profile.pdf') || 'PDF'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                {t('company.profile.clickToView') || 'Click to view'}
                            </div>
                        </div>
                    </div>
                    <a
                        href={form.pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            background: 'linear-gradient(135deg, #608fd3 0%, #4a7bc4 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '10px 16px',
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 4px 12px rgba(74, 123, 196, 0.3)',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(74, 123, 196, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(74, 123, 196, 0.3)';
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                        {t('company.profile.view') || 'View'}
                    </a>
                </div>
            )}



            <CompanyBottomNavigation />
        </div>
    );
};

export default Profile;


